import { useCallback, useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import {
  AlertCircleIcon,
  CopyIcon,
  DownloadIcon,
  FileMinusIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  SendIcon,
  Trash2Icon,
} from 'lucide-react'

import { ApiClientError } from '@ems/api-client/client'
import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { Button } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatDateTime, formatMoney } from '@ems/ui/lib/format'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import {
  invoiceActions,
  invoiceTone,
  isPastDue,
  label,
  outstanding,
} from '#admin/features/billing/api.ts'
import { CompanyStatusCard } from '#admin/features/billing/company-status.tsx'
import { DocumentSkeleton } from '#admin/features/billing/billing-skeletons.tsx'
import { CreditNoteDialog } from '#admin/features/billing/credit-note-dialog.tsx'
import { InvoiceHistoryCard } from '#admin/features/billing/history-card.tsx'
import type { CreditNote } from '#admin/features/billing/credit-notes-api.ts'
import { InvoiceDialog } from '#admin/features/billing/invoice-dialog.tsx'
import {
  BalanceStrip,
  BillToPanel,
  CreditNotesTable,
  DatesPanel,
  LinesTable,
  PaymentsTable,
} from '#admin/features/billing/invoice-panels.tsx'
import {
  useDeleteInvoice,
  useIssueCreditNote,
  useIssueInvoice,
  useReissueInvoice,
  useVoidCreditNote,
  useVoidInvoice,
} from '#admin/features/billing/mutations.ts'
import {
  creditNotesQuery,
  invoicePaymentsQuery,
  invoiceQuery,
} from '#admin/features/billing/queries.ts'
import { RecordPaymentDialog } from '#admin/features/billing/record-payment-dialog.tsx'
import { SendInvoiceButton } from '#admin/features/billing/send-invoice.tsx'
import { NoteDialog } from '#admin/features/registrations/form-dialog.tsx'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/**
 * `/billing/invoices/$invoiceId` — one invoice: what it bills, what has been paid and credited
 * against it, and every verb its status allows. The server refuses each verb again; this keeps
 * the page from offering what it would refuse.
 */
export const Route = createFileRoute('/_authenticated/billing/invoices/$invoiceId')({
  pendingComponent: DocumentSkeleton,
  staticData: { crumb: useInvoiceCrumb },
  loader: async ({ context, params }) => {
    try {
      await Promise.all([
        context.queryClient.ensureQueryData(invoiceQuery(params.invoiceId)),
        context.queryClient.ensureQueryData(invoicePaymentsQuery(params.invoiceId)),
        context.queryClient.ensureQueryData(creditNotesQuery(params.invoiceId)),
        context.queryClient.ensureQueryData(manifestQuery()),
      ])
    } catch (error) {
      // A deleted or unknown invoice is "not here"; a module missing from the plan is also a 404,
      // and that one the ErrorView explains properly.
      if (error instanceof ApiClientError && error.status === 404 && !error.isMissingFeature) {
        throw notFound()
      }
      throw error
    }
  },
  notFoundComponent: NotFoundView,
  component: InvoicePage,
})

function useInvoiceCrumb(): string {
  const { invoiceId } = Route.useParams()
  const { data } = useQuery(invoiceQuery(invoiceId))
  if (data === undefined) return 'Invoice'
  return data.number ?? 'Draft invoice'
}

type Dialog =
  | { kind: 'edit' | 'issue' | 'void' | 'reissue' | 'discard' | 'payment' | 'credit' }
  | { kind: 'issue-credit' | 'void-credit'; note: CreditNote }
  | null

function InvoicePage() {
  const { invoiceId: id } = Route.useParams()
  const { data: invoice } = useSuspenseQuery(invoiceQuery(id))
  const { data: payments } = useSuspenseQuery(invoicePaymentsQuery(id))
  const { data: notes } = useSuspenseQuery(creditNotesQuery(id))
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<Dialog>(null)
  const close = () => setDialog(null)
  const onOpenChange = (open: boolean) => (open ? undefined : close())

  const issue = useIssueInvoice()
  const voidIt = useVoidInvoice()
  const reissue = useReissueInvoice()
  const discard = useDeleteInvoice()
  const issueCredit = useIssueCreditNote()
  const voidCredit = useVoidCreditNote()

  const canManage = can(session, 'billing.manage')
  const actions = invoiceActions(invoice)
  const name = invoice.number ?? 'this draft'
  const currency = manifest?.tenant.currencyCode ?? invoice.currency
  const zone = manifest?.tenant.timezone ?? 'UTC'
  const pastDue = isPastDue(invoice)

  const onIssueCredit = useCallback(
    (note: CreditNote) => setDialog({ kind: 'issue-credit', note }),
    [],
  )
  const onVoidCredit = useCallback(
    (note: CreditNote) => setDialog({ kind: 'void-credit', note }),
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {invoice.number === null ? (
              'Draft invoice'
            ) : (
              <span className="font-mono">{invoice.number}</span>
            )}
            <StatusBadge tone={invoiceTone(invoice.status)}>{label(invoice.status)}</StatusBadge>
          </span>
        }
        description={[invoice.billToName, invoice.billToEmail].filter(Boolean).join(' · ')}
        actions={
          <>
            {/* A plain link, as the registrations CSV export is: the browser shows the download. */}
            <Button asChild variant="outline">
              <a href={`/v1/billing/admin/invoices/${id}/pdf`} download>
                <DownloadIcon />
                Download PDF
              </a>
            </Button>
            {canManage ? (
              <>
                {actions.edit ? (
                  <Button variant="outline" onClick={() => setDialog({ kind: 'edit' })}>
                    <PencilIcon />
                    Edit
                  </Button>
                ) : null}
                {actions.issue ? (
                  <Button onClick={() => setDialog({ kind: 'issue' })}>
                    <SendIcon />
                    Issue
                  </Button>
                ) : null}
                {actions.email ? <SendInvoiceButton invoice={invoice} /> : null}
                {actions.recordPayment ? (
                  <Button onClick={() => setDialog({ kind: 'payment' })}>
                    <PlusIcon />
                    Record payment
                  </Button>
                ) : null}
                {actions.reissue ? (
                  <Button onClick={() => setDialog({ kind: 'reissue' })}>
                    <CopyIcon />
                    Reissue
                  </Button>
                ) : null}
                {actions.credit || actions.void || actions.discard ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" aria-label="More actions">
                        <MoreHorizontalIcon />
                        More
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {actions.credit ? (
                        <DropdownMenuItem onSelect={() => setDialog({ kind: 'credit' })}>
                          <FileMinusIcon />
                          Credit note
                        </DropdownMenuItem>
                      ) : null}
                      {actions.credit && (actions.void || actions.discard) ? (
                        <DropdownMenuSeparator />
                      ) : null}
                      {actions.void ? (
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() => setDialog({ kind: 'void' })}
                        >
                          Void invoice
                        </DropdownMenuItem>
                      ) : null}
                      {actions.discard ? (
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() => setDialog({ kind: 'discard' })}
                        >
                          <Trash2Icon />
                          Discard draft
                        </DropdownMenuItem>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </>
            ) : null}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="this invoice" />}

      {invoice.status === 'voided' ? (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>
            Voided{invoice.voidedAt === null ? '' : ` ${formatDateTime(invoice.voidedAt)}`}
          </AlertTitle>
          {invoice.voidReason === null ? null : (
            <AlertDescription>{invoice.voidReason}</AlertDescription>
          )}
        </Alert>
      ) : null}

      {pastDue ? (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>Past due</AlertTitle>
          <AlertDescription>
            {formatMoney(outstanding(invoice), invoice.currency)} was due{' '}
            {invoice.dueAt === null ? '' : formatDateTime(invoice.dueAt)}.
          </AlertDescription>
        </Alert>
      ) : null}

      {invoice.reissueOfInvoiceId === null ? null : (
        <p className="text-muted-foreground text-sm">
          Replaces a voided invoice.{' '}
          <Link
            to="/billing/invoices/$invoiceId"
            params={{ invoiceId: invoice.reissueOfInvoiceId }}
            className="text-foreground underline"
          >
            Open the original →
          </Link>
        </p>
      )}

      <BalanceStrip invoice={invoice} />

      <CompanyStatusCard invoice={invoice} canManage={canManage} />

      <div className="grid gap-6 md:grid-cols-2">
        <BillToPanel invoice={invoice} />
        <DatesPanel invoice={invoice} />
      </div>

      <LinesTable invoice={invoice} />

      <PaymentsTable
        invoice={invoice}
        payments={payments.items}
        action={
          canManage && actions.recordPayment ? (
            <Button size="sm" variant="outline" onClick={() => setDialog({ kind: 'payment' })}>
              <PlusIcon />
              Record payment
            </Button>
          ) : null
        }
      />

      {notes.items.length > 0 || actions.credit ? (
        <CreditNotesTable
          notes={notes.items}
          canManage={canManage}
          onIssue={onIssueCredit}
          onVoid={onVoidCredit}
        />
      ) : null}

      <InvoiceHistoryCard invoiceId={id} />

      {/* ---- Dialogs. One at a time; each closes when its mutation settles. ---- */}

      {dialog?.kind === 'edit' ? (
        <InvoiceDialog invoice={invoice} currency={currency} zone={zone} onClose={close} />
      ) : null}

      {dialog?.kind === 'payment' ? (
        <RecordPaymentDialog
          invoice={invoice}
          currency={currency}
          zone={zone}
          canUpload={can(session, 'identity.write')}
          onClose={close}
        />
      ) : null}

      {dialog?.kind === 'credit' ? <CreditNoteDialog invoice={invoice} onClose={close} /> : null}

      <ConfirmDialog
        open={dialog?.kind === 'issue'}
        onOpenChange={onOpenChange}
        title="Issue this invoice?"
        description={`It takes the next invoice number and becomes a document the customer holds — ${formatMoney(invoice.total, invoice.currency)} to ${invoice.billToName}. After this it cannot be edited, only voided and reissued.`}
        confirmLabel="Issue invoice"
        busy={issue.isPending}
        onConfirm={() => issue.mutate(id, { onSuccess: close })}
      />

      <NoteDialog
        open={dialog?.kind === 'void'}
        onOpenChange={onOpenChange}
        title={`Void ${name}?`}
        description="The invoice is withdrawn and nothing more is owed on it. It keeps its number, and can be reissued as a new draft."
        confirmLabel="Void invoice"
        noteLabel="Reason"
        placeholder="e.g. Billed to the wrong company."
        required
        destructive
        busy={voidIt.isPending}
        onConfirm={(reason) => voidIt.mutate({ id, reason }, { onSuccess: close })}
      />

      <ConfirmDialog
        open={dialog?.kind === 'reissue'}
        onOpenChange={onOpenChange}
        title={`Reissue ${name}?`}
        description="A new draft is created with the same lines and billing details, pointing back at this one. Review it, then issue it."
        confirmLabel="Create replacement"
        busy={reissue.isPending}
        onConfirm={() =>
          reissue.mutate(id, {
            onSuccess: (replacement) => {
              close()
              void navigate({
                to: '/billing/invoices/$invoiceId',
                params: { invoiceId: replacement.id },
              })
            },
          })
        }
      />

      <ConfirmDialog
        open={dialog?.kind === 'discard'}
        onOpenChange={onOpenChange}
        title="Discard this draft?"
        description="It was never issued, so no number is used and nobody holds a copy. It disappears from every list."
        confirmLabel="Discard draft"
        destructive
        busy={discard.isPending}
        onConfirm={() =>
          discard.mutate(id, {
            onSuccess: () => void navigate({ to: '/billing/invoices' }),
            onSettled: close,
          })
        }
      />

      <ConfirmDialog
        open={dialog?.kind === 'issue-credit'}
        onOpenChange={onOpenChange}
        title="Issue this credit note?"
        description={
          dialog?.kind === 'issue-credit'
            ? `${formatMoney(dialog.note.total, dialog.note.currency)} comes off what ${name} is owed. It takes the next credit note number and cannot be edited after.`
            : ''
        }
        confirmLabel="Issue credit note"
        busy={issueCredit.isPending}
        onConfirm={() => {
          if (dialog?.kind === 'issue-credit')
            issueCredit.mutate(dialog.note.id, { onSuccess: close })
        }}
      />

      <NoteDialog
        open={dialog?.kind === 'void-credit'}
        onOpenChange={onOpenChange}
        title={`Void ${dialog?.kind === 'void-credit' ? (dialog.note.number ?? 'this credit note') : ''}?`}
        description="The credit is withdrawn and the invoice owes that amount again."
        confirmLabel="Void credit note"
        noteLabel="Reason"
        required
        destructive
        busy={voidCredit.isPending}
        onConfirm={(reason) => {
          if (dialog?.kind === 'void-credit') {
            voidCredit.mutate({ id: dialog.note.id, reason }, { onSuccess: close })
          }
        }}
      />
    </div>
  )
}
